import * as React from 'react'
import {
  QuotePaymentScheduleAmountTypeEnum,
  QuotePaymentScheduleDueDateAnchorEnum,
} from '@/gen/cortado/v1/settings_pb'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { CurrencyInput } from '@/components/ui/currency-input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Card } from '@/components/ui/card'
import { DatePicker } from '@/components/shared/date-picker'
import { formatCurrency, dollarsToCents, centsToDollars } from '@/lib/currency-utils'
import { AlertCircle, CheckCircle2, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import {
  buildQuotePaymentScheduleTemplate,
  getQuoteFixedPaymentScheduleBalanceDeltaCents,
  getQuoteFixedPaymentScheduleTotalCents,
  type PaymentScheduleFormValue,
} from '@/lib/payment-schedule-utils'
import { normalizeDueDateAnchor, resolveBeforeEventPreviewDueDate } from '@/lib/quote-payment-schedule-defaults'

interface QuotePaymentScheduleEditorProps {
  schedules: PaymentScheduleFormValue[]
  quoteTotalCents: number
  onSchedulesChange: (schedules: PaymentScheduleFormValue[]) => void
  disabled?: boolean
  onApplyBalanceToLastPayment?: () => void
  onAddBalanceAsNewPayment?: () => void
  depositDueDays?: number
  remainderDueDays?: number
  eventDates?: Array<Date | undefined | null>
  /**
   * Lets each payment pick its due-date anchor (a date, days after acceptance,
   * or days before the first event) instead of only an absolute date.
   */
  editableDueAnchors?: boolean
}

const ANCHOR = QuotePaymentScheduleDueDateAnchorEnum

/** The fields a due-anchor change sets, with a preview due date for relative anchors. */
function dueAnchorUpdates(
  anchor: QuotePaymentScheduleDueDateAnchorEnum,
  offsetDays: number,
  eventDates: Array<Date | undefined | null>,
  currentDueDate: Date,
): Partial<PaymentScheduleFormValue> {
  const offset = Math.max(0, Math.round(offsetDays))
  switch (anchor) {
    case ANCHOR.QUOTE_PAYMENT_SCHEDULE_DUE_DATE_ANCHOR_BEFORE_EVENT:
      return { dueDateAnchor: anchor, dueOffsetDays: offset, dueDate: resolveBeforeEventPreviewDueDate(new Date(), offset, eventDates) }
    case ANCHOR.QUOTE_PAYMENT_SCHEDULE_DUE_DATE_ANCHOR_AFTER_ACCEPTANCE:
      return { dueDateAnchor: anchor, dueOffsetDays: offset, dueDate: new Date() }
    default:
      return { dueDateAnchor: ANCHOR.QUOTE_PAYMENT_SCHEDULE_DUE_DATE_ANCHOR_ABSOLUTE, dueOffsetDays: 0, dueDate: currentDueDate }
  }
}

/**
 * QuotePaymentScheduleEditor - Editor for managing payment schedules on quotes
 *
 * Features:
 * - Quick templates (Full Payment, 50/50 Deposit, Deposit + Remainder, Custom)
 * - Support for percentage-based or fixed amount schedules
 * - Real-time validation (totals must match 100% or quote total)
 * - Prevents mixing percentage and fixed amount schedules
 * - Auto-calculated dollar amounts for percentage schedules
 */
export function QuotePaymentScheduleEditor({
  schedules,
  quoteTotalCents,
  onSchedulesChange,
  disabled = false,
  onApplyBalanceToLastPayment,
  onAddBalanceAsNewPayment,
  depositDueDays,
  remainderDueDays,
  eventDates,
  editableDueAnchors = false,
}: QuotePaymentScheduleEditorProps) {
  // Track selected template to allow re-selection
  const [selectedTemplate, setSelectedTemplate] = React.useState<string>('')

  // Apply quick templates
  const applyTemplate = (template: string) => {
    const newSchedules = buildQuotePaymentScheduleTemplate(template, {
      depositDays: depositDueDays,
      remainderDays: remainderDueDays,
      eventDates,
    })

    // Update schedules immediately - this triggers parent re-render
    onSchedulesChange(newSchedules)
    
    // Set the template value first to show selection, then reset to allow re-selection
    setSelectedTemplate(template)
    
    // Reset select after a brief delay to allow re-selection of the same template
    setTimeout(() => {
      setSelectedTemplate('')
    }, 100)
    
    if (template !== 'custom') {
      toast.success('Template applied')
    }
  }

  // Add new schedule
  const handleAddSchedule = () => {
    const newSequence = schedules.length + 1
    const newSchedule: PaymentScheduleFormValue = {
      sequenceNumber: newSequence,
      description: `Payment ${newSequence}`,
      dueDate: new Date(),
      dueDateAnchor: QuotePaymentScheduleDueDateAnchorEnum.QUOTE_PAYMENT_SCHEDULE_DUE_DATE_ANCHOR_ABSOLUTE,
      dueOffsetDays: 0,
      amountType: QuotePaymentScheduleAmountTypeEnum.QUOTE_PAYMENT_SCHEDULE_AMOUNT_TYPE_PERCENTAGE,
      percentage: 0,
    }
    onSchedulesChange([...schedules, newSchedule])
  }

  // Delete schedule
  const handleDeleteSchedule = (index: number) => {
    const updated = schedules.filter((_, i) => i !== index)
    // Renumber sequence numbers
    const renumbered = updated.map((s, i) => ({
      ...s,
      sequenceNumber: i + 1,
    }))
    onSchedulesChange(renumbered)
  }

  // Update schedule
  const handleUpdateSchedule = (index: number, updates: Partial<PaymentScheduleFormValue>) => {
    const updated = schedules.map((s, i) =>
      i === index ? { ...s, ...updates } : s
    )
    onSchedulesChange(updated)
  }

  // Calculate display amount for a schedule
  const calculateAmount = (schedule: PaymentScheduleFormValue): number => {
    if (schedule.amountType === QuotePaymentScheduleAmountTypeEnum.QUOTE_PAYMENT_SCHEDULE_AMOUNT_TYPE_PERCENTAGE) {
      return Math.round((schedule.percentage || 0) / 100 * quoteTotalCents)
    }
    return schedule.amountCents || 0
  }

  const validationResult = React.useMemo(() => {
    if (schedules.length === 0) {
      return {
        type: 'info' as const,
        message: 'No payment schedule configured. Use a template above or add schedules manually.',
      }
    }

    const hasPercentage = schedules.some(
      (schedule) =>
        schedule.amountType === QuotePaymentScheduleAmountTypeEnum.QUOTE_PAYMENT_SCHEDULE_AMOUNT_TYPE_PERCENTAGE,
    )
    const hasFixed = schedules.some(
      (schedule) =>
        schedule.amountType === QuotePaymentScheduleAmountTypeEnum.QUOTE_PAYMENT_SCHEDULE_AMOUNT_TYPE_FIXED,
    )

    if (hasPercentage && hasFixed) {
      return {
        type: 'warning' as const,
        message: 'Cannot mix percentage and fixed amount payment schedules.',
      }
    }

    if (hasPercentage) {
      const totalPercentage = schedules.reduce((sum, schedule) => sum + (schedule.percentage || 0), 0)
      if (Math.abs(totalPercentage - 100) <= 0.01) {
        return {
          type: 'success' as const,
          message: `Percentages total ${totalPercentage.toFixed(1)}%`,
        }
      }

      return {
        type: 'warning' as const,
        message: `Percentages must total 100% (currently ${totalPercentage.toFixed(1)}%).`,
      }
    }

    const scheduleTotalCents = getQuoteFixedPaymentScheduleTotalCents(schedules) ?? 0
    if (scheduleTotalCents === quoteTotalCents) {
      return {
        type: 'success' as const,
        message: `Total: $${formatCurrency(scheduleTotalCents)} matches quote total`,
      }
    }

    if (scheduleTotalCents < quoteTotalCents) {
      return {
        type: 'warning' as const,
        message: `Total: $${formatCurrency(scheduleTotalCents)} is $${formatCurrency(quoteTotalCents - scheduleTotalCents)} less than quote total of $${formatCurrency(quoteTotalCents)}.`,
      }
    }

    return {
      type: 'warning' as const,
      message: `Total: $${formatCurrency(scheduleTotalCents)} exceeds quote total by $${formatCurrency(scheduleTotalCents - quoteTotalCents)}.`,
    }
  }, [schedules, quoteTotalCents])

  const balanceDelta = getQuoteFixedPaymentScheduleBalanceDeltaCents(schedules, quoteTotalCents) ?? 0
  const showPositiveBalanceActions = balanceDelta > 0 && !disabled &&
    (onApplyBalanceToLastPayment || onAddBalanceAsNewPayment)
  const showNegativeBalanceAction = balanceDelta < 0 && !disabled && Boolean(onApplyBalanceToLastPayment)

  return (
    <div className="space-y-4">
      {/* Quick Templates */}
      <div className="space-y-2">
        <Label>Quick Templates</Label>
        <Select value={selectedTemplate} onValueChange={applyTemplate} disabled={disabled}>
          <SelectTrigger>
            <SelectValue placeholder="Select a template…" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="full-upfront">Full Payment Upfront</SelectItem>
            <SelectItem value="50-50">50/50 Deposit</SelectItem>
            <SelectItem value="deposit-remainder">Deposit + Remainder</SelectItem>
            <SelectItem value="custom">Custom (start from scratch)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {showPositiveBalanceActions && (
        <div className="space-y-3 rounded-lg border border-status-warning-border bg-status-warning-bg p-4 text-sm text-status-warning-fg">
          <div className="flex items-start gap-2">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <p className="font-medium">Apply ${formatCurrency(balanceDelta)} to the payment schedule</p>
              <p className="mt-0.5 text-status-warning-fg">
                Choose how the added quote amount should be collected.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              onClick={onApplyBalanceToLastPayment}
              disabled={!onApplyBalanceToLastPayment}
            >
              Add to Last Payment Item
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onAddBalanceAsNewPayment}
              disabled={!onAddBalanceAsNewPayment}
            >
              Add New Schedule Item
            </Button>
          </div>
        </div>
      )}

      {showNegativeBalanceAction && (
        <div className="space-y-3 rounded-lg border border-status-warning-border bg-status-warning-bg p-4 text-sm text-status-warning-fg">
          <div className="flex items-start gap-2">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <p className="font-medium">Reduce the payment schedule by ${formatCurrency(Math.abs(balanceDelta))}</p>
              <p className="mt-0.5 text-status-warning-fg">
                Apply the lower quote total to the payment schedule before saving.
              </p>
            </div>
          </div>
          <Button
            type="button"
            size="sm"
            onClick={onApplyBalanceToLastPayment}
          >
            Apply Reduction
          </Button>
        </div>
      )}

      {/* Empty state */}
      {schedules.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center border rounded-lg bg-muted/50">
          <p className="text-sm text-muted-foreground mb-4">
            No payment schedule configured. Use a template above or add schedules manually.
          </p>
          <Button type="button" onClick={handleAddSchedule} disabled={disabled}>
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            Add Payment Schedule
          </Button>
        </div>
      )}

      {/* Schedule list */}
      {schedules.length > 0 && (
        <div className="space-y-3">
          {schedules.map((schedule, index) => {
            const calculatedAmount = calculateAmount(schedule)
            const dueDate = schedule.dueDate

            return (
              <Card key={index} className="p-4">
                <div className="space-y-3">
                  {/* Header with sequence number and delete button */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="h-6 w-6 rounded-full bg-primary/10 text-primary text-xs font-medium flex items-center justify-center">
                        {schedule.sequenceNumber}
                      </div>
                      <span className="text-sm font-medium">Payment {schedule.sequenceNumber}</span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteSchedule(index)}
                      disabled={disabled}
                      aria-label={`Delete payment ${schedule.sequenceNumber}`}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>

                  {/* Description */}
                  <div className="space-y-2">
                    <Label htmlFor={`description-${index}`}>Description</Label>
                    <Input
                      id={`description-${index}`}
                      value={schedule.description}
                      onChange={(e) => handleUpdateSchedule(index, { description: e.target.value })}
                      placeholder="Deposit, final payment…"
                      disabled={disabled}
                    />
                  </div>

                  {editableDueAnchors && (
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor={`due-anchor-${index}`}>Due</Label>
                        <Select
                          value={String(normalizeDueDateAnchor(schedule.dueDateAnchor))}
                          onValueChange={(value) =>
                            handleUpdateSchedule(
                              index,
                              dueAnchorUpdates(
                                Number(value) as QuotePaymentScheduleDueDateAnchorEnum,
                                schedule.dueOffsetDays ?? 0,
                                eventDates ?? [],
                                schedule.dueDate,
                              ),
                            )
                          }
                          disabled={disabled}
                        >
                          <SelectTrigger id={`due-anchor-${index}`} aria-label={`Due anchor for payment ${schedule.sequenceNumber}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={String(ANCHOR.QUOTE_PAYMENT_SCHEDULE_DUE_DATE_ANCHOR_ABSOLUTE)}>On a date</SelectItem>
                            <SelectItem value={String(ANCHOR.QUOTE_PAYMENT_SCHEDULE_DUE_DATE_ANCHOR_AFTER_ACCEPTANCE)}>Days after acceptance</SelectItem>
                            <SelectItem value={String(ANCHOR.QUOTE_PAYMENT_SCHEDULE_DUE_DATE_ANCHOR_BEFORE_EVENT)}>Days before the event</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {normalizeDueDateAnchor(schedule.dueDateAnchor) !== ANCHOR.QUOTE_PAYMENT_SCHEDULE_DUE_DATE_ANCHOR_ABSOLUTE && (
                        <div className="space-y-2">
                          <Label htmlFor={`due-offset-${index}`}>Days</Label>
                          <Input
                            id={`due-offset-${index}`}
                            type="number"
                            min="0"
                            step="1"
                            value={schedule.dueOffsetDays ?? 0}
                            onChange={(e) =>
                              handleUpdateSchedule(
                                index,
                                dueAnchorUpdates(
                                  normalizeDueDateAnchor(schedule.dueDateAnchor),
                                  e.target.value === '' ? 0 : Number(e.target.value),
                                  eventDates ?? [],
                                  schedule.dueDate,
                                ),
                              )
                            }
                            disabled={disabled}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Due date */}
                  {(!editableDueAnchors
                    || normalizeDueDateAnchor(schedule.dueDateAnchor) === ANCHOR.QUOTE_PAYMENT_SCHEDULE_DUE_DATE_ANCHOR_ABSOLUTE) && (
                  <div className="space-y-2">
                    <Label htmlFor={`due-date-${index}`}>
                      Due Date <span className="text-destructive">*</span>
                    </Label>
                    <DatePicker
                      value={dueDate}
                      onChange={(date) => {
                        if (date) {
                          handleUpdateSchedule(index, {
                            dueDate: date,
                            dueDateAnchor: QuotePaymentScheduleDueDateAnchorEnum.QUOTE_PAYMENT_SCHEDULE_DUE_DATE_ANCHOR_ABSOLUTE,
                            dueOffsetDays: 0,
                          })
                        }
                      }}
                      disabled={disabled}
                    />
                  </div>
                  )}

                  {/* Amount type and amount - side by side */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor={`amount-type-${index}`}>
                        Amount Type <span className="text-destructive">*</span>
                      </Label>
                      <Select
                        value={schedule.amountType.toString()}
                        onValueChange={(value) => {
                          const newType = parseInt(value) as QuotePaymentScheduleAmountTypeEnum
                          const updates: Partial<PaymentScheduleFormValue> = { amountType: newType }

                          // Clear both fields and set defaults when changing type
                          if (newType === QuotePaymentScheduleAmountTypeEnum.QUOTE_PAYMENT_SCHEDULE_AMOUNT_TYPE_PERCENTAGE) {
                            updates.percentage = 0
                            updates.amountCents = undefined
                          } else if (newType === QuotePaymentScheduleAmountTypeEnum.QUOTE_PAYMENT_SCHEDULE_AMOUNT_TYPE_FIXED) {
                            updates.amountCents = 0
                            updates.percentage = undefined
                          }

                          handleUpdateSchedule(index, updates)
                        }}
                        disabled={disabled}
                      >
                        <SelectTrigger id={`amount-type-${index}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={QuotePaymentScheduleAmountTypeEnum.QUOTE_PAYMENT_SCHEDULE_AMOUNT_TYPE_PERCENTAGE.toString()}>
                            Percentage
                          </SelectItem>
                          <SelectItem value={QuotePaymentScheduleAmountTypeEnum.QUOTE_PAYMENT_SCHEDULE_AMOUNT_TYPE_FIXED.toString()}>
                            Fixed Amount
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Amount input - conditional based on type */}
                    {schedule.amountType === QuotePaymentScheduleAmountTypeEnum.QUOTE_PAYMENT_SCHEDULE_AMOUNT_TYPE_PERCENTAGE && (
                      <div className="space-y-2">
                        <Label htmlFor={`percentage-${index}`}>
                          Percentage <span className="text-destructive">*</span>
                        </Label>
                        <div className="relative">
                          <Input
                            id={`percentage-${index}`}
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            value={schedule.percentage || ''}
                            onChange={(e) => {
                              const value = e.target.value === '' ? 0 : parseFloat(e.target.value)
                              handleUpdateSchedule(index, { percentage: value })
                            }}
                            placeholder="0.00"
                            disabled={disabled}
                            className="pr-8"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                            %
                          </span>
                        </div>
                      </div>
                    )}

                    {schedule.amountType === QuotePaymentScheduleAmountTypeEnum.QUOTE_PAYMENT_SCHEDULE_AMOUNT_TYPE_FIXED && (
                      <div className="space-y-2">
                        <Label htmlFor={`amount-${index}`}>
                          Amount <span className="text-destructive">*</span>
                        </Label>
                        <CurrencyInput
                          id={`amount-${index}`}
                          value={schedule.amountCents ? centsToDollars(schedule.amountCents) : undefined}
                          onChange={(value) => {
                            handleUpdateSchedule(index, { amountCents: dollarsToCents(value) })
                          }}
                          disabled={disabled}
                        />
                      </div>
                    )}
                  </div>

                  {/* Calculated amount display */}
                  <div className="pt-2 border-t">
                    <div className="text-sm text-muted-foreground">
                      Calculated Amount: <span className="font-financial font-medium text-foreground">${formatCurrency(calculatedAmount)}</span>
                    </div>
                  </div>
                </div>
              </Card>
            )
          })}

          {/* Add schedule button */}
          <Button
            type="button"
            variant="outline"
            onClick={handleAddSchedule}
            disabled={disabled}
            className="w-full"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Another Schedule
          </Button>
        </div>
      )}

      <Card
        role={validationResult.type === 'warning' ? 'alert' : 'status'}
        aria-live="polite"
        className={cn(
          'p-4',
          validationResult.type === 'success' && 'bg-status-success-bg border-status-success-border',
          validationResult.type === 'warning' && 'bg-status-warning-bg border-status-warning-border',
          validationResult.type === 'info' && 'bg-status-info-bg border-status-info-border',
        )}
      >
        <div className="flex items-center gap-2">
          {validationResult.type === 'success' && <CheckCircle2 className="h-5 w-5 text-status-success-fg" aria-hidden="true" />}
          {validationResult.type === 'warning' && <AlertCircle className="h-5 w-5 text-status-warning-fg" aria-hidden="true" />}
          {validationResult.type === 'info' && <AlertCircle className="h-5 w-5 text-status-info-fg" aria-hidden="true" />}
          <p className={cn(
            'text-sm',
            validationResult.type === 'success' && 'text-status-success-fg',
            validationResult.type === 'warning' && 'text-status-warning-fg',
            validationResult.type === 'info' && 'text-status-info-fg',
          )}>
            {validationResult.message}
          </p>
        </div>
      </Card>
    </div>
  )
}
